// Focus helpers for modal layers: find tabbables, trap Tab inside a
// container, restore focus to the opener on release.
const TABBABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

export function tabbables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) => !el.closest('[inert]') && el.getAttribute('aria-hidden') !== 'true'
  )
}

export interface TrapOptions {
  /** Element to focus first. Defaults to the first tabbable, else the container. */
  initial?: HTMLElement | null
  /** Return focus to the previously focused element on release. Default true. */
  restore?: boolean
}

/** Trap Tab/Shift+Tab inside `container`. Returns the release function. */
export function trapFocus(container: HTMLElement, options: TrapOptions = {}): () => void {
  const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
  const target = options.initial ?? tabbables(container)[0] ?? container
  if (target === container && !container.hasAttribute('tabindex')) container.tabIndex = -1
  target.focus({ preventScroll: true })

  function onKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Tab') return
    const items = tabbables(container)
    if (items.length === 0) {
      event.preventDefault()
      container.focus()
      return
    }
    const first = items[0]
    const last = items[items.length - 1]
    const active = document.activeElement
    if (event.shiftKey && (active === first || active === container)) {
      event.preventDefault()
      last?.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first?.focus()
    }
  }

  container.addEventListener('keydown', onKeyDown)
  return () => {
    container.removeEventListener('keydown', onKeyDown)
    if (options.restore !== false && previous?.isConnected) previous.focus({ preventScroll: true })
  }
}
